import { useEffect, useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";

const SearchableSecondaryScriptDropdown = ({
  form,
  scriptData,
  handleOnSelect1,
  handleInput,
  getDataForEdit,
  className
}) => {
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [selectedValue, setSelectedValue] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Get the current form values for building the value string
  const getCurrentValue = () => {
    return `${
      form.getValues("secondary_company_name")
        ? form.getValues("secondary_company_name")
        : getDataForEdit.company_name
    }_${
      form.getValues("isin_sec")
        ? form.getValues("isin_sec")
        : getDataForEdit.isin_sec
    }_${
      form.getValues("secondary_security_type")
        ? form.getValues("secondary_security_type")
        : ""
    }_${
      form.getValues("secondary_face_value")
        ? form.getValues("secondary_face_value")
        : ""
    }_${
      form.getValues("sec_scrip_id")
        ? form.getValues("sec_scrip_id")
        : ""
    }`;
  };

  // Calculate dropdown position
  const calculateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      
      setDropdownPosition({
        top: rect.bottom + scrollTop,
        left: rect.left + scrollLeft,
        width: rect.width
      });
    }
  };

  // Initialize the component with current values
  useEffect(() => {
    const currentValue = getCurrentValue();
    setSelectedValue(currentValue);
    
    // Set initial display text (show company name)
    const displayName = form.getValues("secondary_company_name") || getDataForEdit.company_name;
    setSearchText(displayName || "");
  }, [form, getDataForEdit]);

  // Filter data based on search text
  useEffect(() => {
    if (!scriptData) {
      setFilteredData([]);
      return;
    }

    if (searchText && searchText !== "") {
      const filtered = scriptData.filter((item) =>
        item?.script_name?.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(scriptData);
    }
  }, [searchText, scriptData]);

  // Handle input change
  const handleOnChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    setIsDropdownVisible(value.length > 0);
    
    if (value.length > 0) {
      calculateDropdownPosition();
    }
  };

  // Handle item selection
  const handleSelect = (item) => {
    const selectedValue = `${item?.script_name}_${item.isin}_${
      item?.script_type_name
    }_${
      item?.nse_face_value
        ? item?.nse_face_value
        : item?.bse_face_value
    }_${item.id}`;

    setSelectedValue(selectedValue);
    setSearchText(item?.script_name);
    setIsDropdownVisible(false);

    // Directly update the form value
    form.setValue("company_name", selectedValue);

    // Create a synthetic event to maintain compatibility with handleOnSelect1
    const syntheticEvent = {
      target: {
        value: selectedValue,
        name: "company_name"
      }
    };

    // Call the original handler
    if (handleOnSelect1) {
      handleOnSelect1(syntheticEvent);
    }
  };

  // Handle default option selection
  const handleSelectDefault = () => {
    const defaultValue = `${
      form.getValues("company_name")
        ? form.getValues("company_name")
        : getDataForEdit.company_name
    }_${
      form.getValues("isin_sec")
        ? form.getValues("isin_sec")
        : getDataForEdit.isin_sec
    }_${""}_${""}_${""}`;

    setSelectedValue(defaultValue);
    setSearchText(getDataForEdit.company_name);
    setIsDropdownVisible(false);

    // Directly update the form value
    form.setValue("company_name", defaultValue);

    const syntheticEvent = {
      target: {
        value: defaultValue,
        name: "company_name"
      }
    };

    if (handleOnSelect1) {
      handleOnSelect1(syntheticEvent);
    }
  };

  // Handle focus
  const handleFocus = () => {
    setIsDropdownVisible(true);
    calculateDropdownPosition();
  };

  // Handle blur with delay to allow clicks
  const handleBlur = () => {
    setTimeout(() => {
      setIsDropdownVisible(false);
    }, 200);
  };

  // Handle scroll and resize events to update dropdown position
  useEffect(() => {
    if (isDropdownVisible) {
      const handleScroll = () => calculateDropdownPosition();
      const handleResize = () => calculateDropdownPosition();
      
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isDropdownVisible]);

  const isDisabled = ["B", "S", "C", "D", "G"].includes(handleInput.corpaction_tag);

  const dropdownContent = isDropdownVisible && !isDisabled && (
    <div 
      ref={dropdownRef}
      className="absolute max-h-[200px] overflow-hidden overflow-y-auto rounded bg-white border border-gray-300 shadow-lg whitespace-nowrap" 
      style={{ 
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        minWidth: dropdownPosition.width,
        zIndex: 10000 
      }}
    >
      {filteredData.length > 0 ? (
        filteredData.map((item, index) => (
          <div
            key={index}
            onClick={() => handleSelect(item)}
            className="cursor-pointer border-b bg-white p-2 text-[#636363] hover:bg-gray-100"
          >
            {item?.script_name}
          </div>
        ))
      ) : (
        searchText && (
          <div className="p-2 text-gray-500">No scripts found</div>
        )
      )}
    </div>
  );

  return (
    <div className="relative w-full">
      <select
        {...form.register("company_name")}
        onChange={() => {}}
        className="hidden"
      >
        <option value={selectedValue}>{searchText}</option>
      </select>

      <div className={`${className || "w-full"} flex flex-col rounded bg-neutral-100`}>
        <div className="rounded bg-transparent px-2 py-[1px]">
          <input
            ref={inputRef}
            value={searchText}
            placeholder="Search scripts..."
            className="w-full bg-transparent py-[.7rem] text-[#636363] placeholder:italic focus:outline-none"
            onChange={handleOnChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={isDisabled}
          />
        </div>
      </div>

      {/* Render dropdown using portal to avoid z-index issues */}
      {typeof document !== 'undefined' && createPortal(
        dropdownContent,
        document.body
      )}
    </div>
  );
};

export default SearchableSecondaryScriptDropdown;